import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'

import { describeApiError } from '@common/utils/apiErrors'
import {
  useDeviceQueryMutationState,
  useValveCommission,
  type ValveTarget,
} from '@data/device/device'

const COMMISSION_COOLDOWN_SECONDS = 90

// Shared by the Device Modal's Valves tab and the Test Bench modal's Valves tab: sends the
// commission, confirms it once the message mutation settles, holds the button in a cooldown
// while the sequence runs, then calls `onCooldownEnd` so the caller can refresh the device.
// The Operate Valves page keeps its own copy, which also resets the cooldown when the
// selected device changes. All of them hold for the same 90 seconds the commission panel
// tells the user to expect, so the refresh lands after the sequence has finished.
//
// Success is read off the shared device-message mutation state rather than this mutation's
// own result, as before extraction. Both tabs unmount when another tab is shown, so a firmware
// update or reset sent from the commands tab is not mistaken for a commission.
export const useValveCommissioning = (target: ValveTarget, onCooldownEnd: () => void) => {
  const {
    deviceSendingMessage,
    error,
  } = useDeviceQueryMutationState()
  const { mutateAsync: commissionValves } = useValveCommission()

  const [cooldown, setCooldown] = useState(0)
  const wasSendingMessage = useRef(false)
  const wasInCooldown = useRef(false)
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (wasSendingMessage.current && !deviceSendingMessage && !error) {
      notifications.show({
        title: 'Success',
        message: 'Commission valves message sent successfully.',
        color: 'green',
        icon: <IconCheck size={16} />,
      })
      setCooldown(COMMISSION_COOLDOWN_SECONDS)
      wasInCooldown.current = true
      cooldownIntervalRef.current = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownIntervalRef.current!)
            cooldownIntervalRef.current = null
            return 0
          }
          return prev - 1
        })
      }, 1_000)
    }
    wasSendingMessage.current = deviceSendingMessage
  }, [deviceSendingMessage, error])

  useEffect(() => {
    if (cooldown === 0 && wasInCooldown.current) {
      wasInCooldown.current = false
      onCooldownEnd()
    }
  }, [cooldown])

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current)
    }
  }, [])

  const handleCommissionValves = useCallback(async () => {
    try {
      await commissionValves(target)
    } catch (error) {
      notifications.show({
        title: 'Valve commissioning failed',
        message: describeApiError(error, 'Failed to commission the valves.'),
        color: 'red',
      })
    }
  }, [commissionValves, target])

  return {
    cooldown,
    isSending: deviceSendingMessage,
    handleCommissionValves,
  }
}
